import React, { Component } from "react";
import ProjectTitle from "./projectPage/ProjectTitle";
import ProjectProgressBar from "./projectPage/ProjectProgressBar";
import ProjectProgressView from "../components/ProjectProgressView";
import ProjectHistoryView from "../components/ProjectHistoryView";
import Modal from "../components/Modal";
import ProjectActionsMenu from "./projectPage/ProjectActionsMenu";
import ChaptersContainer from "./projectPage/ChaptersContainer";
import AgentPanel from "./projectPage/AgentPanel";
import ProjectCoachPanel from "./projectPage/ProjectCoachPanel";
import ProjectStepsButtons from "./projectPage/ProjectStepsButtons";
import StoryBibleModal from "./projectPage/StoryBibleModal";
import KanbanModal from "./projectPage/KanbanModal";
import PlotModal from "./projectPage/PlotModal";
import ProjectSceneSearchModal from "./projectPage/ProjectSceneSearchModal";
import { Icon } from "../components/Icon";
import ClipboardNote from "./chapterPage/ClipboardNote";
import TabsMenu from "../../components/TabsMenu";
import { withRouter } from "../navigation/withNavigation";
import { urlMap, getAuthHeaders } from "../../utils";
import { fetchProjectById, fetchProjectTypeById, loadContext as loadContextService } from "../../services/projects";
import { fetchSubscriptionStatus, openSubscriptionTabInWallet } from "../../utils/subscriptionAccess";
import { AppContext } from "../../context/AppContext";
import { createPortal } from "react-dom";

class ProjectPage extends Component {
  static contextType = AppContext;
  agentPanelRef = React.createRef();
  chaptersContainerRef = React.createRef();
  clipboardNoteRef = React.createRef();
  state = {
    loading: true,
    error: null,
    selectedProject: null,

    
    projectType: null,
    projectTypeLoading: false,
    projectTypeError: null,

    // preload chapters (from loadContext) and pass to ChaptersContainer
    initialChapters: null,

    // just for header display
    chaptersCount: 0,
    
    // Character refresh trigger
    charactersRefreshTrigger: 0,
    
    // Chapters refresh trigger
    chaptersRefreshTrigger: 0,

    // Side panel: null | 'characters' | 'plot' | 'plotHoles'
    sidePanel: null,

    // Project-level issues count (for badge)
    projectIssuesCount: 0,

    // Step 4 (PlanRewriteStep) estimate in minutes
    step4EstimateMinutes: null,

    // Story bible modal
    showStoryBibleModal: false,

    // Issues kanban modal
    showIssuesModal: false,

    // Plot modal (unified: plot, summaries, characters, structure/themes, rewrite plan)
    showPlotModal: false,
    plotModalInitialTab: 'plot',

    // Search modal (project-wide chapter text search)
    showSearchModal: false,

    // Progress feed modal
    showProgressModal: false,
    progressModalTab: 'history',

    // Persist selected item in project tools row
    selectedToolsMenuItem: null,

    // Subscription gate for premium tools
    isPro: false,
    proLoading: true,
  };

  // Centralized setter to keep local state and AppContext in sync
  setSelectedProject = (project) => {
    this.setState({ selectedProject: project }, () => {
      if (project) this.context?.setCurrentProject?.(project);
      else this.context?.clearCurrentProject?.();
    });
  };

 
  componentDidMount() {
    const projectId = this.props.params?.projectId;
    if (!projectId) {
      this.props.navigate("/home");
      return;
    }

    const shouldPreselectAgent = localStorage.getItem('storyclub.preselectAgentMenu') === '1';
    if (shouldPreselectAgent) {
      this.setState({ selectedToolsMenuItem: 'agent', sidePanel: 'agent' });
      localStorage.removeItem('storyclub.preselectAgentMenu');
    }

    // Keyboard shortcut: 'o' then 'i' opens progress modal
    this._keySequenceBuffer = '';
    this._keySequenceTimer = null;
    this._handleKeySequence = (e) => {
      const tag = (e.target?.tagName || '').toLowerCase();
      if (tag === 'input' || tag === 'textarea' || e.target?.isContentEditable) return;
      this._keySequenceBuffer += e.key;
      clearTimeout(this._keySequenceTimer);
      if (this._keySequenceBuffer.endsWith('oi')) {
        this._keySequenceBuffer = '';
        this.setState({ showProgressModal: true });
        return;
      }
      this._keySequenceTimer = setTimeout(() => { this._keySequenceBuffer = ''; }, 800);
    };
    document.addEventListener('keydown', this._handleKeySequence);

    this.loadSubscription();

    // Prefer project from context if it matches the route param
    const ctxProject = this.context?.state?.currentProject;
    if (ctxProject && ctxProject.id === projectId) {
      this.setSelectedProject(ctxProject);
      const typeId = ctxProject.project_type_key;
      const ctxType = typeId
        ? (this.context?.state?.projectTypes?.[typeId] || this.context?.state?.currentProjectType)
        : null;
      if (ctxType && (ctxType.id === typeId || ctxType.key === typeId || ctxType.uuid === typeId)) {
        this.setState({ loading: false, error: null, projectType: ctxType });
      } else {
        this.setState({ loading: false, error: null }, () => this.maybeLoadProjectType(ctxProject));
      }
    } else {
      this.loadContext(projectId);
    }

    // Auto-open issues modal when navigated with #issues hash
    if (window.location.hash === '#issues') {
      this.setState({ showIssuesModal: true });
      window.history.replaceState(null, '', window.location.pathname);
    }
  }

  componentWillUnmount() {
    if (this._handleKeySequence) {
      document.removeEventListener('keydown', this._handleKeySequence);
      clearTimeout(this._keySequenceTimer);
    }
  }

  componentDidUpdate(prevProps) {
    const prevId = prevProps.params?.projectId;
    const currId = this.props.params?.projectId;
    if (prevId !== currId && currId) {
      // On route change, try context first, then fetch
      const ctxProject = this.context?.state?.currentProject;
      if (ctxProject && ctxProject.id === currId) {
        this.setSelectedProject(ctxProject);
        const typeId = ctxProject.project_type_key;
        const ctxType = typeId
          ? (this.context?.state?.projectTypes?.[typeId] || this.context?.state?.currentProjectType)
          : null;
        if (ctxType && (ctxType.id === typeId || ctxType.key === typeId || ctxType.uuid === typeId)) {
          this.setState({ loading: false, error: null, projectType: ctxType });
        } else {
          this.setState({ loading: false, error: null }, () => this.maybeLoadProjectType(ctxProject));
        }
      } else {
        this.loadContext(currId);
      }
    }

    // Detect project type change from context (e.g. Menu's ProjectTypePicker)
    const ctxProject = this.context?.state?.currentProject;
    const localProject = this.state.selectedProject;
    if (
      ctxProject &&
      localProject &&
      ctxProject.id === localProject.id &&
      ctxProject.project_type_key !== localProject.project_type_key
    ) {
      this.setSelectedProject(ctxProject);
      this.maybeLoadProjectType(ctxProject);
    }
  }

  // Loader uses the shared frontend service and reseeds context
  loadContext = async (projectId) => {
    this.setState({
      loading: true,
      error: null,
      // Clear local and global selection while loading
      selectedProject: null,
      projectType: null,
      projectTypeLoading: false,
      projectTypeError: null,
      initialChapters: null,
      chaptersCount: 0,
      step4EstimateMinutes: null,
    });
    // Also clear from context immediately
    this.context?.clearCurrentProject?.();

    try {
      const { project, projectType, chapters } = await loadContextService(projectId, this.context);
      this.setSelectedProject(project);
      this.setState({
        projectType: projectType || null,
        projectTypeLoading: false,
        initialChapters: Array.isArray(chapters) ? chapters : null,
        error: null,
        loading: false,
      });
      this.fetchProjectIssuesCount(project?.id);
    } catch (e) {
      console.error("Load project failed:", e);
      this.setState({
        loading: false,
        error: e?.message || "Failed to load project",
        selectedProject: null,
      });
      this.context?.clearCurrentProject?.();
    }
  };

   // ---- helpers ----
  isUUID = (v) => /^[0-9a-fA-F-]{36}$/.test(v);

  pickProjectTypeUrl = (id) => {
   
    const fromList =
      urlMap?.listProjectTypes ? `${urlMap.listProjectTypes}/${id}` : null;
    if (fromList) return fromList;

    return `/core/project-types/${id}`;
  };

  safeParseJson = async (res) => {
    const ct = (res.headers.get("content-type") || "").toLowerCase();
    if (ct.includes("application/json")) return res.json();
    const text = await res.text().catch(() => "");
    throw new Error(
      `EXPECTED_JSON_GOT_${ct || "unknown"}${text ? ` · ${text.slice(0, 120)}` : ""}`
    );
  };


  maybeLoadProjectType = async (project) => {
    const id = project?.project_type_key;
    if (!id || !this.isUUID(id)) {
      this.setState({ projectType: null, projectTypeLoading: false, projectTypeError: null });
      return;
    }
    const fromCtx = this.context?.state?.projectTypes?.[id] || this.context?.state?.currentProjectType;
    if (fromCtx && (fromCtx.id === id || fromCtx.key === id || fromCtx.uuid === id)) {
      this.setState({ projectType: fromCtx, projectTypeLoading: false, projectTypeError: null });
      return;
    }
    this.setState({ projectTypeLoading: true, projectTypeError: null });
    try {
      const projectType = await fetchProjectTypeById(id);
      this.setState({ projectType, projectTypeLoading: false });
      this.context?.setProjectType?.(projectType);
    } catch (e) {
      console.error("Load project type failed:", e);
      this.setState({
        projectType: null,
        projectTypeLoading: false,
        projectTypeError: String(e?.message || e),
      });
    }
  };

  /** Single-call updater for title and/or project_type_key (and other fields). */
  updateProject = async (patch /* { title?, project_type_key?, archived? } */) => {
    const project = this.state.selectedProject;
    if (!project) throw new Error("NO_PROJECT");
    const headers = { "Content-Type": "application/json", ...getAuthHeaders() };

    const res = await fetch(`${(urlMap?.projectEndpoint || "/core/projects")}/${project.id}`, {
      method: "PUT",
      headers,
      credentials: "include",
      body: JSON.stringify(patch),
    });

    if (!res.ok) {
      const payload = await res.json().catch(() => ({}));
      const msg = payload?.error || `HTTP_${res.status}`;
      throw new Error(msg);
    }

    const updated = await res.json();
    // Keep context in sync with any updates
    this.setSelectedProject(updated);
    this.maybeLoadProjectType(updated);
    return updated;
  };

  /** Refetch the project to get fresh metadata (e.g., after agent updates plot) */
  refreshProject = async () => {
    const projectId = this.state.selectedProject?.id;
    if (!projectId) return;

    try {
      const freshProject = await fetchProjectById(projectId);
      this.setSelectedProject(freshProject);
      this.fetchProjectIssuesCount(projectId);
      return freshProject;
    } catch (error) {
      console.error('Failed to refresh project:', error);
      throw error;
    }
  };

  fetchProjectIssuesCount = async (projectId) => {
    if (!projectId) return;
    try {
      const [chapterRes, projectRes] = await Promise.all([
        fetch(urlMap.getProjectIssues(projectId), { headers: getAuthHeaders(), credentials: 'include' }),
        fetch(urlMap.getStructuralThematicIssues(projectId), { headers: getAuthHeaders(), credentials: 'include' }),
      ]);
      let count = 0;
      if (chapterRes.ok) {
        const issues = await chapterRes.json();
        count += Array.isArray(issues) ? issues.filter((i) => i.status !== 'done' && i.status !== 'pending').length : 0;
      }
      if (projectRes.ok) {
        const pIssues = await projectRes.json();
        count += Array.isArray(pIssues) ? pIssues.filter((i) => i.status !== 'dismissed').length : 0;
      }
      this.setState({ projectIssuesCount: count });
    } catch {
      // non-critical, ignore
    }
  };

  // Progress from project.metadata.rewritePlan.progressSummary if available
  // Otherwise falls back to project.progressSummary or workloadProgressPercent
  // Demo data if neither available

  onUpdateTitle = async (newTitle) => {
    const updated = await this.updateProject({ title: newTitle });
    return updated;
  };

  handleCloseChapters = () => {
    const pid = this.state.selectedProject?.id || this.props.params?.projectId;
    this.props.navigate(pid ? `/project/${pid}/library` : "/home", { replace: true });
  };

  handleChaptersCountChange = (n) => {
    if (typeof n === "number") this.setState({ chaptersCount: n });
  };

  refreshCharacters = () => {
    // Increment trigger to force CharactersPanel to reload
    this.setState((prev) => ({
      charactersRefreshTrigger: prev.charactersRefreshTrigger + 1,
    }));
  };

  refreshChapters = () => {
    // Increment trigger to force ChaptersContainer to reload
    this.setState((prev) => ({
      chaptersRefreshTrigger: prev.chaptersRefreshTrigger + 1,
    }));
  };

  toggleSidePanel = (key) => {
    this.setState((prev) => ({
      sidePanel: prev.sidePanel === key ? null : key,
    }));
  };

  selectToolsMenuItem = (itemKey, action) => {
    this.setState({ selectedToolsMenuItem: itemKey }, () => {
      action?.();
    });
  };

  getSeededDemoPercent = (seedInput) => {
    const seed = String(seedInput || 'storyclub-project');
    let hash = 2166136261;
    for (let i = 0; i < seed.length; i += 1) {
      hash ^= seed.charCodeAt(i);
      hash += (hash << 1) + (hash << 4) + (hash << 7) + (hash << 8) + (hash << 24);
    }
    const unsigned = hash >>> 0;
    return (unsigned % 71) + 15;
  };

  getProjectProgressData = () => {
    const { selectedProject } = this.state;
    const project = selectedProject || {};

    // Try to get progress from rewrite plan metadata first
    const planProgressSummary = project?.metadata?.rewritePlan?.progressSummary;
    const planEstimatedPercent = Number(project?.metadata?.rewritePlan?.estimatedCompletionPercentage);
    const workloadProgressFromSummary = Number(project?.progressSummary?.projectProgressPercent);
    const workloadProgressFromField = Number(project.workloadProgressPercent);
    
    const apiPercent = Number.isFinite(planProgressSummary?.projectProgressPercent)
      ? planProgressSummary.projectProgressPercent
      : Number.isFinite(planEstimatedPercent)
        ? planEstimatedPercent
      : Number.isFinite(workloadProgressFromSummary)
        ? workloadProgressFromSummary
        : workloadProgressFromField;

    if (!Number.isFinite(apiPercent)) {
      return {
        percent: 0,
        isDraftComplete: false,
        isDemo: false,
        infoText: 'Progress reflects estimated remaining effort to finish the draft, including rewrite and polish work.',
      };
    }

    const percent = Math.max(0, Math.min(100, Math.round(apiPercent)));

    return {
      percent,
      isDraftComplete: !!project.isDraftComplete || percent >= 100,
      isDemo: false,
      infoText: 'Progress reflects estimated remaining effort to finish the draft, including rewrite and polish work.',
    };
  };

  openSearchModal = () => {
    this.setState({ showSearchModal: true });
  };

  closeSearchModal = () => {
    this.setState((prev) => ({
      showSearchModal: false,
      selectedToolsMenuItem: prev.selectedToolsMenuItem === 'search' ? null : prev.selectedToolsMenuItem,
    }));
  };

  loadSubscription = async () => {
    try {
      const status = await fetchSubscriptionStatus();
      const isPro = !!status?.isPro;
      this.setState((prev) => ({
        isPro,
        proLoading: false,
        sidePanel: !isPro && (prev.sidePanel === 'agent' || prev.sidePanel === 'coach') ? null : prev.sidePanel,
      }));
    } catch {
      this.setState({ isPro: false, proLoading: false });
    }
  };

  handleProLockedToolClick = () => {
    openSubscriptionTabInWallet();
  };

  render() {
    const {
      loading,
      error,
      selectedProject,
      projectType,
      projectTypeLoading,
      projectTypeError,
      chaptersCount,
      charactersRefreshTrigger,
      chaptersRefreshTrigger,
      sidePanel,
      projectIssuesCount,
      selectedToolsMenuItem,
      showSearchModal,
      initialChapters,
      isPro,
      proLoading,
    } = this.state;

    if (loading) return <div style={{ padding: 16, color: "#6b7280" }}>Loading…</div>;
    if (error) return <div style={{ padding: 16, color: "#b91c1c" }}>{error}</div>;
    if (!selectedProject) return null;

    const isArchived = !!selectedProject.archived_at;
    const projectProgress = this.getProjectProgressData();
    const metadata = selectedProject?.metadata || {};
    const hasAgentResults = !!(
      metadata.plot
      || metadata.characters
      || metadata.plotHoles
      || metadata.rewritePlan
      || metadata.storyBible
    );
    const toolsProLocked = !isPro;
    const agentDisabled = proLoading || toolsProLocked;
    const coachDisabled = agentDisabled || !hasAgentResults;
    const projectTitleSlot = typeof document !== 'undefined'
      ? document.getElementById('topbar-project-title-slot')
      : null;

    return (
      <div className={`projects${sidePanel ? ' panel-open' : ''}`} style={{ padding: 12 }}>
        {projectTitleSlot && createPortal(
          <ProjectTitle
            project={selectedProject}
            onClose={this.handleCloseChapters}
            onUpdateTitle={this.onUpdateTitle}
            onRefreshProject={this.refreshProject}
            disabled={isArchived}
          />,
          projectTitleSlot,
        )}

        {/* Full-width archive banner ABOVE the header for clean alignment */}
        {isArchived && (
          <div className="archived-banner" role="note" aria-live="polite" style={{ marginBottom: 12 }}>
            This project is archived (view-only). Use the <strong>⋮ actions</strong> menu to restore.
          </div>
        )}

        {/* Header row: title + icons + actions */}
        <div className="library-header">
          <div className="library-header-title-group">
            <div className="project-meta-steps-row">
              <div className="project-meta">
                {new Date(selectedProject.created_at).toLocaleDateString()}
                {typeof chaptersCount === 'number' ? ` • ${chaptersCount} chapters` : null}
                {isArchived && (
                  <>
                    {' • '}
                    <span className="status-chip archived" title="Project is archived">Archived</span>
                  </>
                )}
              </div>
              <ProjectStepsButtons
                onPlotClick={(tab) => this.setState({ showPlotModal: true, plotModalInitialTab: tab || 'plot' })}
                onProgressClick={() => this.setState({ showProgressModal: true })}
                onIssuesClick={() => this.setState({ showIssuesModal: true })}
                projectIssuesCount={projectIssuesCount}
                progressPercent={projectProgress.percent}
              />
              <div className="project-meta-actions-end">
                <ProjectActionsMenu
                  project={selectedProject}
                  onUpdated={(updatedProject) => this.setSelectedProject(updatedProject)}
                  showHint={isArchived}
                />
              </div>
            </div>
            <div className="project-tools-cluster">
              <div className="project-add-chapter-group">
                <button
                  type="button"
                  className="btn primary project-add-chapter-btn"
                  title={isArchived ? 'Restore project to add chapter' : 'Add Chapter'}
                  aria-label={isArchived ? 'Restore project to add chapter' : 'Add Chapter'}
                  disabled={isArchived}
                  onClick={() => {
                    if (isArchived) {
                      alert('This project is archived. Restore it first to add chapters.');
                      return;
                    }
                    this.chaptersContainerRef.current?.createChapter?.();
                  }}
                >
                  + Add Chapter
                </button>
              </div>

              <div className="project-tools-group">
                <ClipboardNote ref={this.clipboardNoteRef} projectId={selectedProject.id} hideTrigger />
                <div className="project-tools-inline" aria-label="Project tools" role="toolbar">
                  <button
                    type="button"
                    className={`btn subtle project-tool-btn${selectedToolsMenuItem === 'clipboard' ? ' is-selected' : ''}`}
                    title="Open clipboard"
                    onClick={() => this.selectToolsMenuItem('clipboard', () => this.clipboardNoteRef.current?.toggle?.())}
                  >
                    <Icon.Clipboard />
                    <span>Clipboard</span>
                  </button>
                  <button
                    type="button"
                    className={`btn subtle project-tool-btn${selectedToolsMenuItem === 'search' ? ' is-selected' : ''}`}
                    title="Search scenes"
                    onClick={() => {
                      if (selectedToolsMenuItem === 'search') {
                        this.closeSearchModal();
                        return;
                      }
                      this.selectToolsMenuItem('search', this.openSearchModal);
                    }}
                  >
                    <Icon.SearchLoop />
                    <span>Search</span>
                  </button>
                  <button
                    type="button"
                    className={`btn subtle project-tool-btn${sidePanel === 'agent' ? ' is-active' : ''}${selectedToolsMenuItem === 'agent' ? ' is-selected' : ''}`}
                    title={
                      proLoading
                        ? 'Checking subscription status...'
                        : (agentDisabled
                          ? 'Agent is available on Pro plan'
                          : (sidePanel === 'agent' ? 'Hide agent' : 'Show agent'))
                    }
                    onClick={() => {
                      if (proLoading) return;
                      if (toolsProLocked) {
                        this.handleProLockedToolClick();
                        return;
                      }
                      this.selectToolsMenuItem('agent', () => this.toggleSidePanel('agent'));
                    }}
                    aria-disabled={agentDisabled}
                  >
                    <span className="project-tool-icon-wrap" aria-hidden="true">
                      <Icon.Agent />
                    </span>
                    <span>Agent</span>
                    {toolsProLocked && !proLoading ? (
                      <span className="project-tool-btn-overlay" aria-hidden="true">
                        <span className="project-tool-btn-overlay-lock">
                          <svg viewBox="0 0 20 20" width="10" height="10" fill="currentColor" focusable="false">
                            <path fillRule="evenodd" d="M10 1a4 4 0 0 0-4 4v2H5a2 2 0 0 0-2 2v7a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V9a2 2 0 0 0-2-2h-1V5a4 4 0 0 0-4-4Zm2 6V5a2 2 0 1 0-4 0v2h4Z" clipRule="evenodd" />
                          </svg>
                        </span>
                      </span>
                    ) : null}
                  </button>
                  <button
                    type="button"
                    className={`btn subtle project-tool-btn${sidePanel === 'coach' ? ' is-active' : ''}${selectedToolsMenuItem === 'coach' ? ' is-selected' : ''}`}
                    title={
                      proLoading
                        ? 'Checking subscription status...'
                        : (toolsProLocked
                          ? 'Coach is available on Pro plan'
                          : (coachDisabled
                            ? 'Coach is disabled until an agent step is run'
                            : (sidePanel === 'coach' ? 'Hide coach' : 'Show coach')))
                    }
                    onClick={() => {
                      if (proLoading) return;
                      if (toolsProLocked) {
                        this.handleProLockedToolClick();
                        return;
                      }
                      if (!hasAgentResults) {
                        return;
                      }
                      this.selectToolsMenuItem('coach', () => this.toggleSidePanel('coach'));
                    }}
                    aria-disabled={coachDisabled}
                  >
                    <span className="project-tool-icon-wrap" aria-hidden="true">
                      <Icon.Coach />
                    </span>
                    <span>Coach</span>
                    {toolsProLocked && !proLoading ? (
                      <span className="project-tool-btn-overlay" aria-hidden="true">
                        <span className="project-tool-btn-overlay-lock">
                          <svg viewBox="0 0 20 20" width="10" height="10" fill="currentColor" focusable="false">
                            <path fillRule="evenodd" d="M10 1a4 4 0 0 0-4 4v2H5a2 2 0 0 0-2 2v7a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V9a2 2 0 0 0-2-2h-1V5a4 4 0 0 0-4-4Zm2 6V5a2 2 0 1 0-4 0v2h4Z" clipRule="evenodd" />
                          </svg>
                        </span>
                      </span>
                    ) : null}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Main content: chapters + agent panel side by side */}
        <div className="project-content-row">
          <div className="project-chapters-col">
            <ChaptersContainer
              ref={this.chaptersContainerRef}
              project={selectedProject}
              projectType={projectType}
              projectTypeLoading={projectTypeLoading}
              projectTypeError={projectTypeError}
              chapters={this.state.initialChapters}
              readOnly={isArchived}
              navigate={this.props.navigate}
              onClose={this.handleCloseChapters}
              onCountChange={this.handleChaptersCountChange}
              refreshTrigger={chaptersRefreshTrigger}
            />
          </div>

          {sidePanel === 'agent' && (
            <AgentPanel
              ref={this.agentPanelRef}
              project={selectedProject}
              onCharactersExtracted={this.refreshCharacters}
              onChaptersSummarized={this.refreshChapters}
              onProjectMetadataChanged={this.refreshProject}
              sidePanel={sidePanel}
              onTogglePanel={this.toggleSidePanel}
              charactersRefreshTrigger={charactersRefreshTrigger}
              onIssuesCountChanged={() => this.fetchProjectIssuesCount(selectedProject.id)}
              onOpenRewritePlan={() => this.setState({ showPlotModal: true, plotModalInitialTab: 'rewritePlan' })}
              notificationContext={this.props.notificationContext}
            />
          )}

          {sidePanel === 'coach' && (
            <ProjectCoachPanel
              projectId={selectedProject.id}
              onClose={() => this.toggleSidePanel('coach')}
            />
          )}
        </div>

        <StoryBibleModal
          isOpen={this.state.showStoryBibleModal}
          onClose={() => this.setState({ showStoryBibleModal: false })}
          project={selectedProject}
          onRefreshProject={this.refreshProject}
          onTriggerAgentStep={(fromStep, extraData) => {
            this.setState({ sidePanel: 'agent' }, () => {
              setTimeout(() => {
                this.agentPanelRef.current?.triggerRunFromStep(fromStep, extraData);
              }, 100);
            });
          }}
        />

        <KanbanModal
          isOpen={this.state.showIssuesModal}
          onClose={() => this.setState({ showIssuesModal: false })}
          project={selectedProject}
          onIssuesCountChanged={() => this.fetchProjectIssuesCount(selectedProject.id)}
        />

        <PlotModal
          isOpen={this.state.showPlotModal}
          onClose={() => this.setState({ showPlotModal: false })}
          project={selectedProject}
          refreshTrigger={charactersRefreshTrigger}
          initialTab={this.state.plotModalInitialTab}
          onRefreshProject={this.refreshProject}
          onTriggerAgentStep={(fromStep, extraData) => {
            this.setState({ sidePanel: 'agent' }, () => {
              // Small delay to let AgentPanel mount if it wasn't open
              setTimeout(() => {
                this.agentPanelRef.current?.triggerRunFromStep(fromStep, extraData);
              }, 100);
            });
          }}
          onIssuesCountChanged={() => this.fetchProjectIssuesCount(selectedProject.id)}
        />

        <ProjectSceneSearchModal
          isOpen={showSearchModal}
          onClose={this.closeSearchModal}
          projectId={selectedProject.id}
          chapters={this.chaptersContainerRef.current?.getAllChapters?.() || initialChapters || []}
          navigate={this.props.navigate}
        />

        {this.state.showProgressModal && (
          <Modal isOpen onClose={() => this.setState({ showProgressModal: false })}>
            <div className="public-feed public-feed--dashboard-modal" style={{ background: 'none', minHeight: 0, padding: 0, width: 'min(1040px, 92vw)' }}>
              <header className="public-feed__header public-progress-modal__header">
                <div className="public-progress-modal__title-row">
                  <h1 className="public-progress-view__modal-title">
                    {(selectedProject?.title || 'Project')} Writing progress
                  </h1>
                  <div className="public-progress-modal__bar">
                    <ProjectProgressBar {...projectProgress} />
                  </div>
                </div>
                <div className="public-progress-modal__tabs-row">
                  <TabsMenu
                    className="public-progress-modal__tabs"
                    ariaLabel="Writing progress views"
                    activeKey={this.state.progressModalTab}
                    onChange={(key) => this.setState({ progressModalTab: key })}
                    tabs={[
                      { key: 'history', label: 'History' },
                      { key: 'reflection', label: 'Reflection' },
                    ]}
                  />
                </div>
              </header>
              <div className="public-progress-modal__body">
                {this.state.progressModalTab === 'history' ? (
                  <ProjectHistoryView projectId={selectedProject.id} />
                ) : (
                  <ProjectProgressView projectId={selectedProject.id} />
                )}
              </div>
            </div>
          </Modal>
        )}
      </div>
    );
  }
}

export default withRouter(ProjectPage);
